import { useEffect, useState, useRef } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * Version watcher.
 *
 * Two independent signals, because they fail in opposite directions:
 *
 *  1. BACKEND — `/api/version` returns `<semver>+<build_id>`, where build_id
 *     is a hash of the backend's Python. Changes on any backend deploy.
 *  2. FRONTEND — the hashed bundle name in `asset-manifest.json`, fetched
 *     with cache busting. Changes on any frontend build.
 *
 * Oct 2026 — signal 2 was missing, and that is why the banner went quiet.
 * The build id hashes `*.py` ONLY, so a frontend-only release (a settings
 * reorg, the camera work, the impound panel — most UI work) produced the
 * IDENTICAL id and nobody was ever told to refresh. Operators carried on
 * running weeks-old JavaScript against a current backend and assumed the
 * changes hadn't shipped.
 *
 * Feb 2026 — "Update Now" busts the SW cache before reloading; a plain
 * reload kept serving the old cached bundle, so the banner reappeared 60s
 * later forever.
 *
 * Banner sits top-center, prominent — bottom-center was too easy to miss.
 */
const POLL_MS = 60_000;

/** Test hook: lets a browser test drive the poll without waiting a minute.
 *  Never set in normal use, so production always polls at POLL_MS. */
const pollMs = () =>
  (typeof window !== "undefined" && Number(window.__TOWHOOK_VERSION_POLL_MS)) || POLL_MS;

/** Hashed entry bundle for the build currently being SERVED (not the one
 *  running in this tab). Null when unavailable — the CRA dev server doesn't
 *  emit a manifest, and a missing manifest must never fake an update. */
async function servedBundleId() {
  try {
    const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const m = await res.json();
    return m?.files?.["main.js"] || m?.entrypoints?.join("|") || null;
  } catch {
    return null;
  }
}

async function forceFreshReload() {
  // Best-effort cleanup across all supported platforms. Each step is
  // wrapped so a browser that lacks the API doesn't crash the reload —
  // the URL cachebust at the end is the universal fallback that works
  // even if every other step is a no-op.
  //
  // Coverage:
  //   • Windows/macOS Chrome/Edge/Firefox — all APIs supported
  //   • iOS Safari 11.1+ — caches API supported; cachebust critical
  //     because iOS Safari sometimes ignores Cache-Control on JS
  //   • Android Chrome / installed PWA — same
  //   • Ancient / stripped browsers — silently skip missing APIs,
  //     still cachebust URL
  try {
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      // Update — never unregister: unregistering destroys this phone's
      // push sign-up, so job alerts silently stopped after every deploy.
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all((regs || []).map((r) => r.update().catch(() => false)));
    }
  } catch { /* best-effort */ }
  try {
    if (typeof window !== "undefined" && "caches" in window && window.caches) {
      const keys = await window.caches.keys();
      await Promise.all((keys || []).map((k) => window.caches.delete(k).catch(() => false)));
    }
  } catch { /* best-effort */ }
  // Universal fallback. A URL the browser has never seen cannot be served
  // from any cache tier — disk, memory, or SW. This step alone is enough
  // on every platform we support; the two above are belt-and-braces.
  try {
    const base = window.location.href.split("#")[0].split("?")[0];
    window.location.href = `${base}?v=${Date.now()}`;
  } catch {
    // Absolute last resort — this can only fail in exotic sandboxes.
    window.location.reload();
  }
}

export default function VersionWatcher() {
  const bootVersionRef = useRef(null);
  const bootSemverRef = useRef(null);
  const bootBundleRef = useRef(null);
  const [latestSemver, setLatestSemver] = useState(null);
  const [outdated, setOutdated] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      // Frontend build — catches UI-only releases, which the backend hash
      // cannot see.
      const bundle = await servedBundleId();
      if (cancelled) return;
      if (bundle) {
        if (!bootBundleRef.current) {
          bootBundleRef.current = bundle;
        } else if (bundle !== bootBundleRef.current) {
          setOutdated(true);
        }
      }
      // Backend build.
      try {
        const { data } = await api.get("/version");
        if (cancelled) return;
        if (!bootVersionRef.current) {
          bootVersionRef.current = data.version;
          bootSemverRef.current = data.semver || data.version;
        } else if (data.version && data.version !== bootVersionRef.current) {
          setLatestSemver(data.semver || data.version);
          setOutdated(true);
        }
      } catch { /* silent — surface only when we know we're behind */ }
    };
    check();
    const t = setInterval(check, pollMs());
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!outdated) return null;

  const from = bootSemverRef.current || "?";
  const to = latestSemver || "?";
  // A frontend-only release has no new backend semver to quote, so don't
  // render "v? is live" at the operator.
  const sameSemver = !latestSemver || from === to;

  return (
    <div
      className="fixed top-16 left-1/2 -translate-x-1/2 z-[100] max-w-lg w-[94%] rounded-md border-2 border-blue-400 bg-blue-50 dark:bg-blue-950/95 dark:border-blue-500 shadow-2xl px-4 py-3 flex items-center gap-3 animate-in slide-in-from-top-4"
      data-testid="version-update-banner"
    >
      <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
          New update available
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-300">
          {sameSemver
            ? <>A fresh version of Tow-Hook is ready. Tap Update Now to install.</>
            : <>You&apos;re on v{from} · v{to} is live. Tap Update Now to install.</>}
        </div>
      </div>
      <Button
        size="sm"
        disabled={updating}
        onClick={async () => {
          setUpdating(true);
          await forceFreshReload();
        }}
        className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white font-semibold shrink-0"
        data-testid="version-reload-btn"
      >
        <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (updating ? "animate-spin" : "")} />
        {updating ? "Updating…" : "Update Now"}
      </Button>
    </div>
  );
}
